/* eslint-disable react/prop-types, react/jsx-handler-names */

import React from "react";
import Select, { components as selectBaseComponents } from "react-select";
import { withStyles, WithStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import { AnalyseSelectOption } from "./AnalyseNativeSelect";

export type ReactSelectOption = {
  value: string;
  label: string;
};

type Props = WithStyles<typeof styles> & {
  id: string;
  label: string;
  value: string;
  options: AnalyseSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  emptyLabel?: string;
  loading?: boolean;
  minWidth?: number;
  maxWidth?: number;
  flex?: string;
  theme: Theme;
};

const MENU_EXTRA_WIDTH = 48;
const VIEWPORT_PADDING = 8;

const getClampedMenuPortalStyle = (
  base: any,
  state: any,
  menuMinWidth: number
) => {
  if (typeof window === "undefined") {
    return { ...base, zIndex: 1300 };
  }

  const viewportWidth = window.innerWidth;
  const availableWidth = viewportWidth - VIEWPORT_PADDING * 2;
  const menuWidth = Math.max(
    state.rect.width,
    Math.min(menuMinWidth, availableWidth)
  );

  let left = state.rect.left;
  if (left + menuWidth > viewportWidth - VIEWPORT_PADDING) {
    left = viewportWidth - VIEWPORT_PADDING - menuWidth;
  }
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING;
  }

  return {
    ...base,
    left,
    width: menuWidth,
    minWidth: Math.min(menuMinWidth, availableWidth),
    maxWidth: availableWidth,
    zIndex: 1300,
  };
};

const clampMenuPortalWithinViewport = (node: HTMLElement | null) => {
  if (!node || typeof window === "undefined") return;

  node.style.transform = "";
  const rect = node.getBoundingClientRect();
  let deltaX = 0;
  let deltaY = 0;

  if (rect.right > window.innerWidth - VIEWPORT_PADDING) {
    deltaX = window.innerWidth - VIEWPORT_PADDING - rect.right;
  }
  if (rect.left + deltaX < VIEWPORT_PADDING) {
    deltaX = VIEWPORT_PADDING - rect.left;
  }
  if (rect.bottom > window.innerHeight - VIEWPORT_PADDING) {
    deltaY = window.innerHeight - VIEWPORT_PADDING - rect.bottom;
  }
  if (rect.top + deltaY < VIEWPORT_PADDING) {
    deltaY = VIEWPORT_PADDING - rect.top;
  }

  if (deltaX !== 0 || deltaY !== 0) {
    node.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
  }
};

const measureLabelWidth = (text: string): number => {
  if (!text) return 0;
  if (typeof document === "undefined") {
    return text.length * 7;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return text.length * 7;
  ctx.font =
    '400 13px Roboto, "Helvetica Neue", Arial, sans-serif';
  return ctx.measureText(text).width;
};

const computeMenuMinWidth = (
  options: AnalyseSelectOption[],
  extraLabels: string[],
  controlMinWidth = 120
): number => {
  const allLabels = [...options.map((o) => o.label), ...extraLabels];
  const longest = allLabels.reduce(
    (max, label) => Math.max(max, measureLabelWidth(label)),
    0
  );
  return Math.max(controlMinWidth, Math.ceil(longest) + MENU_EXTRA_WIDTH);
};

const SELECT_BORDER_RADIUS = 8;

const styles = (theme: Theme) =>
  createStyles({
    root: {
      minWidth: 120,
      width: "100%",
    },
    /** Appliqué sur la racine MuiOutlinedInput via InputProps.className */
    outlinedInput: {
      borderRadius: SELECT_BORDER_RADIUS,
      overflow: "hidden",
      "& fieldset": {
        borderRadius: `${SELECT_BORDER_RADIUS}px !important`,
      },
      "&:hover fieldset": {
        borderColor: `${theme.palette.secondary.light} !important`,
      },
      "&.Mui-focused fieldset": {
        borderColor: `${theme.palette.secondary.main} !important`,
        borderWidth: "1px !important",
      },
    },
    input: {
      display: "flex",
      alignItems: "center",
      padding: 0,
      fontSize: "0.8125rem",
    },
    valueContainer: {
      display: "flex",
      flexWrap: "nowrap",
      flex: 1,
      alignItems: "center",
      overflow: "hidden",
      padding: "0 8px",
      position: "relative",
      minHeight: 34,
      height: 34,
      boxSizing: "border-box",
    },
    noOptionsMessage: {
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
      fontSize: "0.8125rem",
    },
    singleValue: {
      fontSize: "0.8125rem",
    },
    placeholder: {
      position: "absolute",
      left: 8,
      fontSize: "0.8125rem",
    },
    paper: {
      marginTop: theme.spacing.unit / 2,
      width: "100%",
      maxWidth: "100%",
      boxShadow: theme.shadows[1],
    },
  });

const inputComponent = ({ inputRef, ...props }: any) => (
  <div ref={inputRef} {...props} />
);

const Control = (props: any) => (
  <TextField
    fullWidth
    margin="dense"
    variant="outlined"
    InputProps={{
      className: props.selectProps.classes.outlinedInput,
      style: {
        height: 40,
        boxSizing: "border-box",
        alignItems: "center",
        padding: 0,
      },
      inputComponent,
      inputProps: {
        className: props.selectProps.classes.input,
        inputRef: props.innerRef,
        children: props.children,
        ...props.innerProps,
      },
    }}
    {...props.selectProps.textFieldProps}
  />
);

const Input = (props: any) => <selectBaseComponents.Input {...props} />;

const Option = (props: any) => (
  <MenuItem
    buttonRef={props.innerRef}
    selected={props.isFocused}
    component="div"
    style={{
      fontWeight: props.isSelected ? 500 : 400,
      fontSize: "0.8125rem",
      whiteSpace: "nowrap",
    }}
    {...props.innerProps}
  >
    {props.children}
  </MenuItem>
);

const Placeholder = (props: any) => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.placeholder}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

const SingleValue = (props: any) => (
  <Typography
    className={props.selectProps.classes.singleValue}
    noWrap
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

const ValueContainer = (props: any) => (
  <div className={props.selectProps.classes.valueContainer}>
    {props.children}
  </div>
);

const NoOptionsMessage = (props: any) => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.noOptionsMessage}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

const MenuList = (props: any) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    let node: HTMLElement | null = listRef.current;
    let fixedParent: HTMLElement | null = null;
    while (node) {
      if (window.getComputedStyle(node).position === "fixed") {
        fixedParent = node;
        break;
      }
      node = node.parentElement;
    }
    clampMenuPortalWithinViewport(fixedParent);
  });

  return (
    <selectBaseComponents.MenuList
      {...props}
      innerRef={(ref: HTMLDivElement | null) => {
        listRef.current = ref;
        const existingRef = props.innerRef;
        if (typeof existingRef === "function") {
          existingRef(ref);
        } else if (existingRef) {
          existingRef.current = ref;
        }
      }}
    />
  );
};

const Menu = (props: any) => (
  <Paper
    square
    className={props.selectProps.classes.paper}
    style={{ minWidth: props.selectProps.menuMinWidth }}
    {...props.innerProps}
  >
    {props.children}
  </Paper>
);

const selectComponents = {
  Control,
  Input,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

const toReactSelectOptions = (
  options: AnalyseSelectOption[]
): ReactSelectOption[] =>
  options.map((opt) => ({
    value: String(opt.value),
    label: opt.label,
  }));

const AnalyseReactSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
  emptyLabel,
  loading = false,
  minWidth,
  maxWidth,
  flex,
  classes,
  theme,
}: Props) => {
  const reactSelectOptions = toReactSelectOptions(options);
  const selectedOption =
    reactSelectOptions.find((opt) => opt.value === value) || null;

  const placeholder = loading ? "Chargement..." : emptyLabel || label;

  const menuMinWidth = React.useMemo(
    () =>
      computeMenuMinWidth(
        options,
        [placeholder, "Aucun resultat"],
        minWidth || 120
      ),
    [options, placeholder, minWidth]
  );

  const isInputActive = (state: any) => {
    const { selectProps } = state;
    const inputValue = selectProps?.inputValue;
    return Boolean(
      selectProps?.menuIsOpen ||
        selectProps?.isFocused ||
        (inputValue != null && String(inputValue).length > 0)
    );
  };

  const selectStyles = {
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: theme.palette.text.primary,
      fontSize: "0.8125rem",
      opacity: 1,
      visibility: "visible",
    }),
    valueContainer: (base: any) => ({
      ...base,
      flexWrap: "nowrap",
      position: "relative",
      padding: "0 8px",
      minHeight: 34,
      height: 34,
      alignItems: "center",
    }),
    singleValue: (base: any, state: any) => ({
      ...base,
      maxWidth: "100%",
      margin: 0,
      lineHeight: "20px",
      visibility: isInputActive(state) ? "hidden" : "visible",
    }),
    control: (base: any) => ({
      ...base,
      minHeight: 40,
      height: 40,
      borderWidth: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: 40,
      alignSelf: "stretch",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: 4,
    }),
    clearIndicator: (base: any) => ({
      ...base,
      padding: 4,
    }),
    menu: (base: any) => ({
      ...base,
      width: "max-content",
      minWidth: menuMinWidth,
      maxWidth: `calc(100vw - ${VIEWPORT_PADDING * 2}px)`,
    }),
    menuPortal: (base: any, state: any) =>
      getClampedMenuPortalStyle(base, state, menuMinWidth),
  };

  return (
    <div
      className={classes.root}
      style={{
        ...(minWidth != null ? { minWidth } : {}),
        ...(maxWidth != null ? { maxWidth } : {}),
        ...(flex != null ? { flex } : {}),
      }}
    >
      <Select
        inputId={id}
        name={id}
        classes={classes}
        menuMinWidth={menuMinWidth}
        styles={selectStyles}
        options={reactSelectOptions}
        components={selectComponents}
        value={selectedOption}
        onChange={(option) => {
          onChange(option ? option.value : "");
        }}
        placeholder={placeholder}
        isClearable
        isDisabled={disabled || loading}
        isLoading={loading}
        isSearchable
        menuPlacement="auto"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        menuPosition="fixed"
        noOptionsMessage={() => "Aucun resultat"}
        textFieldProps={{
          id,
          hiddenLabel: true,
        }}
      />
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(AnalyseReactSelect);
